import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { Button, Input, Table } from 'reactstrap';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4maps from '@amcharts/amcharts4/maps';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import cities from './mock';
import roteNdaoGeo from '../../assets/jsonData/kab_Rote.json';
import TulufaliLogo from '../Icons/tulufali_logo.svg';
import s from './am4chartMap.module.scss';
import './tooltip.css';
import {
  DESA_BANTUAN_TAHUN,
  BANTUAN_LIST_TAHUN,
  API_TIMEOUT,
  NIRLABA_DASHBOARD_DESA,
  NIRLABA_DASHBOARD_LIST_BANTUAN,
} from '../../utils';
import { normalizeForMatch, resolveKecamatanIncludedFilter } from '../../utils/mapGeoMerge';
import axios from 'axios';

const TAGLINE = 'Tata Kelola Bantuan Terintegrasi Untuk Penanggulangan Kemiskinan Rote Ndao';

const formatRupiah = (n) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n || 0);
};

const toSmallCapsText = (t) =>
  (t || '').toString().toLowerCase().replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const emptyMetrics = () => ({ jumlah_penerima: 0, total_uang: 0, total_desil12: 0 });

const addMetrics = (target, row) => {
  target.jumlah_penerima += Number(row.jumlah_penerima) || 0;
  target.total_uang += Number(row.total_uang) || 0;
  target.total_desil12 += Number(row.total_desil12) || 0;
};

const sortBantuanItems = (items) =>
  [...items].sort(
    (a, b) =>
      (Number(b.total_uang) || 0) - (Number(a.total_uang) || 0) ||
      String(a.bantuan || '').localeCompare(String(b.bantuan || ''), 'id')
  );

const buildRincianHtml = (items) => {
  if (!items || !items.length) return '';
  const maxShow = 8;
  const shown = items.slice(0, maxShow);
  const rest = items.length - shown.length;
  const rows = shown
    .map((it) => {
      const name = escapeHtml(toSmallCapsText(it.bantuan || 'Lainnya'));
      const org = (Number(it.jumlah_penerima) || 0).toLocaleString('id-ID');
      return `<div class="map-tooltip-rincian-row"><span class="map-tooltip-rincian-name">${name}</span><span class="map-tooltip-rincian-meta">${org} org</span></div>`;
    })
    .join('');
  const more =
    rest > 0 ? `<div class="map-tooltip-rincian-more">+${rest} jenis lainnya</div>` : '';
  return `<div class="map-tooltip-rincian"><div class="map-tooltip-rincian-title">Jenis bantuan</div>${rows}${more}</div>`;
};

const hasBantuanBreakdown = (rows) =>
  Array.isArray(rows) && rows.some((r) => String(r?.bantuan || '').trim() !== '');

const buildBantuanYearSummary = (rows) => {
  const map = new Map();
  (rows || []).forEach((row) => {
    const name = String(row.bantuan || '').trim();
    if (!name) return;
    const prev = map.get(name) || { bantuan: name, ...emptyMetrics() };
    addMetrics(prev, row);
    map.set(name, prev);
  });
  return sortBantuanItems([...map.values()]);
};

const Am4chartMap = forwardRef(function Am4chartMap(
  {
    tahun = '2024',
    fixedBantuan = null,
    compactHeader = false,
    hidePerDesaTable = false,
    mapDomId: mapDomIdProp = null,
    hideMapChrome = false,
    customizationEmbed = false,
    externalBantuan = null,
    /**
     * Opsional (halaman pengaturan peta): samakan tingkat peta & filter kecamatan dengan peta DTSEN.
     * { mapLevel, kecamatanIncludedKeys, colors?: { none, stroke? } }
     */
    layoutSettings = null,
    source = 'pemerintah',
    lembagaId = null,
  },
  ref
) {
  const isMitraMap = source === 'mitra';
  const [listDesa, setListDesa] = useState([]);
  const [listBantuan, setListBantuan] = useState([]);
  const [internalBantuan, setInternalBantuan] = useState('all');
  const isFixed = Boolean(fixedBantuan);
  const isExternal = Boolean(externalBantuan);
  const selectedBantuan = isFixed ? fixedBantuan : isExternal ? externalBantuan.value : internalBantuan;
  const mapDomId =
    mapDomIdProp ||
    (isMitraMap ? 'mapMitra' : isFixed ? 'mapBantuanDetail' : 'map');
  const [isLoadingMap, setIsLoadingMap] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const am4ChartRef = useRef(null);
  const mapWrapRef = useRef(null);
  const tahunRef = useRef(tahun);
  const features = roteNdaoGeo.features;
  const [mapCombine, setMapCombine] = useState();
  const [danaThresholds, setDanaThresholds] = useState({ threshold1: 0, threshold2: 0 });
  const pdfExportRef = useRef({ mode: 'desa', rows: [], bantuanSummary: [] });
  const yearBantuanSummaryRef = useRef([]);
  const [bantuanTableSnapshot, setBantuanTableSnapshot] = useState({ mode: 'desa', rows: [] });

  const desaToKec = {};
  (roteNdaoGeo.features || []).forEach((f) => {
    const d = String(f.properties?.DESA || '').trim().toUpperCase();
    if (d) desaToKec[d] = f.properties?.KECAMATAN || '';
  });
  const getKecamatan = (desa) => desaToKec[String(desa || '').trim().toUpperCase()] || '-';

  const DANA_COLORS = {
    none: '#e0e0e0',
    low: '#ffe082',
    medium: '#f5bf23',
    high: '#b38600',
  };

  const combineData = () => {
    const mapLevel = layoutSettings?.mapLevel === 'kecamatan' ? 'kecamatan' : 'desa';
    const colorsNone = layoutSettings?.colors?.none || DANA_COLORS.none;

    const allKecKeysFromGeo = new Set();
    for (let gi = 0; gi < features.length; gi++) {
      const kk = normalizeForMatch(features[gi].properties?.KECAMATAN);
      if (kk) allKecKeysFromGeo.add(kk);
    }
    const kecFilter = layoutSettings
      ? resolveKecamatanIncludedFilter(mapLevel, layoutSettings.kecamatanIncludedKeys, allKecKeysFromGeo)
      : { active: false, set: null };

    const breakdownActive = hasBantuanBreakdown(listDesa);
    const desaLookup = new Map();
    const kecBantuan = new Map();
    listDesa.forEach((item) => {
      const key = String(item?.desa || '').toUpperCase().trim();
      if (!key) return;
      const bantuanName = String(item.bantuan || '').trim();
      const prev = desaLookup.get(key) || {
        desa: item.desa,
        ...emptyMetrics(),
        breakdown: [],
      };
      addMetrics(prev, item);
      if (item._desaDesil) prev._desaDesil = Number(item._desaDesil) || prev._desaDesil || 0;
      if (bantuanName) {
        const found = prev.breakdown.find(
          (b) => String(b.bantuan || '').trim().toUpperCase() === bantuanName.toUpperCase()
        );
        if (found) addMetrics(found, item);
        else {
          prev.breakdown.push({
            bantuan: bantuanName,
            jumlah_penerima: Number(item.jumlah_penerima) || 0,
            total_uang: Number(item.total_uang) || 0,
            total_desil12: Number(item.total_desil12) || 0,
          });
        }
      }
      desaLookup.set(key, prev);
    });

    const kecMetric = new Map();
    listDesa.forEach((row) => {
      const kRaw =
        row.kecamatan != null && String(row.kecamatan).trim() !== ''
          ? String(row.kecamatan).trim()
          : getKecamatan(row.desa);
      const k = normalizeForMatch(kRaw);
      if (!k) return;
      const prev = kecMetric.get(k) || {
        total_uang: 0,
        jumlah_penerima: 0,
        total_desil12: 0,
        label: kRaw || k,
      };
      prev.total_uang += Number(row.total_uang) || 0;
      prev.jumlah_penerima += Number(row.jumlah_penerima) || 0;
      prev.total_desil12 += Number(row.total_desil12) || 0;
      if (kRaw && !prev.label) prev.label = kRaw;
      kecMetric.set(k, prev);

      const bantuanName = String(row.bantuan || '').trim();
      if (!bantuanName) return;
      const inner = kecBantuan.get(k) || new Map();
      const prevB = inner.get(bantuanName) || { bantuan: bantuanName, ...emptyMetrics() };
      addMetrics(prevB, row);
      inner.set(bantuanName, prevB);
      kecBantuan.set(k, inner);
    });

    desaLookup.forEach((agg) => {
      if (!(Number(agg.total_desil12) > 0) && agg._desaDesil) {
        agg.total_desil12 = agg._desaDesil;
      }
    });
    kecMetric.forEach((m, k) => {
      if (Number(m.total_desil12) > 0) return;
      let sum = 0;
      desaLookup.forEach((agg) => {
        const kk = normalizeForMatch(getKecamatan(agg.desa));
        if (kk === k) sum += Number(agg.total_desil12) || 0;
      });
      m.total_desil12 = sum;
    });

    const valuesForTiers = [];
    if (mapLevel === 'kecamatan') {
      kecMetric.forEach((m, k) => {
        if (kecFilter.active && kecFilter.set && !kecFilter.set.has(k)) return;
        if (m.total_uang > 0) valuesForTiers.push(m.total_uang);
      });
    } else {
      desaLookup.forEach((d) => {
        const kecRow = normalizeForMatch(getKecamatan(d.desa));
        if (kecFilter.active && kecFilter.set && kecRow && !kecFilter.set.has(kecRow)) return;
        const v = Number(d.total_uang) || 0;
        if (v > 0) valuesForTiers.push(v);
      });
    }
    valuesForTiers.sort((a, b) => a - b);

    let threshold1 = 0;
    let threshold2 = 0;
    if (valuesForTiers.length >= 3) {
      const i1 = Math.floor(valuesForTiers.length / 3);
      const i2 = Math.floor((2 * valuesForTiers.length) / 3);
      threshold1 = valuesForTiers[i1];
      threshold2 = valuesForTiers[i2];
    } else if (valuesForTiers.length === 2) {
      threshold1 = valuesForTiers[0];
      threshold2 = valuesForTiers[1];
    } else if (valuesForTiers.length === 1) {
      threshold1 = threshold2 = valuesForTiers[0];
    }

    setDanaThresholds({ threshold1, threshold2 });

    const getTierColor = (totalUang) => {
      if (!totalUang || totalUang <= 0) return colorsNone;
      if (totalUang <= threshold1) return DANA_COLORS.low;
      if (totalUang <= threshold2) return DANA_COLORS.medium;
      return DANA_COLORS.high;
    };

    const desaku = [];
    for (let i = 0; i < features.length; i++) {
      const desa = features[i];
      const desaName = (desa.properties?.DESA || '').toUpperCase().trim();
      const kecKey = normalizeForMatch(desa.properties?.KECAMATAN);
      const dimmedKec =
        kecFilter.active && kecFilter.set && kecKey && !kecFilter.set.has(kecKey);

      if (mapLevel === 'kecamatan') {
        const m = kecMetric.get(kecKey) || {
          total_uang: 0,
          jumlah_penerima: 0,
          total_desil12: 0,
          label: desa.properties?.KECAMATAN || '',
        };
        const tu = dimmedKec ? 0 : m.total_uang;
        desa.properties.penerimaBantuan = dimmedKec ? 0 : m.jumlah_penerima;
        desa.properties.totalUang = tu;
        desa.properties.totalDesil12 = dimmedKec ? 0 : m.total_desil12;
        desa.properties.totalUangFormatted = formatRupiah(dimmedKec ? 0 : m.total_uang);
        desa.properties.color = dimmedKec ? colorsNone : getTierColor(tu);
        const kecItems = sortBantuanItems([...(kecBantuan.get(kecKey)?.values() || [])]);
        desa.properties.tooltipBantuanRincianHtml =
          !dimmedKec && breakdownActive ? buildRincianHtml(kecItems) : '';
        desa.properties.tooltipBantuanSubtitle = dimmedKec
          ? ''
          : 'Nilai agregat per kecamatan (satu warna per kecamatan)';
        desa.properties._bantuanLevel = 'kecamatan';
      } else {
        desa.properties.tooltipBantuanSubtitle = '';
        desa.properties._bantuanLevel = 'desa';
        if (dimmedKec) {
          desa.properties.penerimaBantuan = 0;
          desa.properties.totalUang = 0;
          desa.properties.totalDesil12 = 0;
          desa.properties.totalUangFormatted = formatRupiah(0);
          desa.properties.color = colorsNone;
          desa.properties.tooltipBantuanRincianHtml = '';
        } else {
          const bantuanDesa = desaLookup.get(desaName);
          if (bantuanDesa) {
            const totalUang = Number(bantuanDesa.total_uang) || 0;
            desa.properties.penerimaBantuan = Number(bantuanDesa.jumlah_penerima) || 0;
            desa.properties.totalUang = totalUang;
            desa.properties.totalDesil12 = Number(bantuanDesa.total_desil12) || 0;
            desa.properties.totalUangFormatted = formatRupiah(bantuanDesa.total_uang);
            desa.properties.color = getTierColor(totalUang);
            desa.properties.tooltipBantuanRincianHtml = breakdownActive
              ? buildRincianHtml(sortBantuanItems(bantuanDesa.breakdown || []))
              : '';
          } else {
            desa.properties.penerimaBantuan = 0;
            desa.properties.totalUang = 0;
            desa.properties.totalDesil12 = 0;
            desa.properties.totalUangFormatted = formatRupiah(0);
            desa.properties.color = colorsNone;
            desa.properties.tooltipBantuanRincianHtml = '';
          }
        }
      }

      desaku.push(desa);
    }

    setMapCombine({
      type: 'FeatureCollection',
      features: desaku,
    });

    let snapshot = { mode: 'desa', breakdown: false, rows: [], groups: [] };
    if (mapLevel === 'kecamatan') {
      const rows = [];
      kecMetric.forEach((m, k) => {
        if (kecFilter.active && kecFilter.set && !kecFilter.set.has(k)) return;
        rows.push({ kecKey: k, ...m });
      });
      rows.sort((a, b) => String(a.label || '').localeCompare(String(b.label || ''), 'id'));
      const groups = breakdownActive
        ? rows.map((row) => ({
            ...row,
            items: sortBantuanItems([...(kecBantuan.get(row.kecKey)?.values() || [])]),
          }))
        : [];
      snapshot = { mode: 'kecamatan', breakdown: breakdownActive, rows, groups };
    } else {
      const filteredDesa = [];
      const seenDesa = new Set();
      desaLookup.forEach((agg) => {
        const kr = normalizeForMatch(getKecamatan(agg.desa));
        if (kecFilter.active && kecFilter.set && kr && !kecFilter.set.has(kr)) return;
        const desaKey = String(agg.desa || '').toUpperCase().trim();
        if (!desaKey || seenDesa.has(desaKey)) return;
        seenDesa.add(desaKey);
        filteredDesa.push(agg);
      });
      filteredDesa.sort((a, b) =>
        String(a.desa || '').localeCompare(String(b.desa || ''), 'id')
      );
      const rows = filteredDesa.map((agg) => ({
        desa: agg.desa,
        jumlah_penerima: agg.jumlah_penerima,
        total_uang: agg.total_uang,
        total_desil12: agg.total_desil12,
      }));
      const groups = breakdownActive
        ? filteredDesa.map((agg) => ({
            desa: agg.desa,
            kecamatan: getKecamatan(agg.desa),
            jumlah_penerima: agg.jumlah_penerima,
            total_uang: agg.total_uang,
            total_desil12: agg.total_desil12,
            items: sortBantuanItems(agg.breakdown || []),
          }))
        : [];
      snapshot = { mode: 'desa', breakdown: breakdownActive, rows, groups };
    }

    let bantuanSummary = [];
    if (breakdownActive) {
      bantuanSummary = buildBantuanYearSummary(listDesa);
      yearBantuanSummaryRef.current = bantuanSummary;
    } else if ((yearBantuanSummaryRef.current || []).length) {
      bantuanSummary = yearBantuanSummaryRef.current;
    } else {
      const totals = (listDesa || []).reduce((acc, row) => {
        addMetrics(acc, row);
        return acc;
      }, emptyMetrics());
      const label =
        selectedBantuan && selectedBantuan !== 'all' ? selectedBantuan : 'Semua bantuan';
      if (totals.jumlah_penerima || totals.total_uang) {
        bantuanSummary = [{ bantuan: label, ...totals }];
      }
    }
    snapshot.bantuanSummary = bantuanSummary;
    pdfExportRef.current = snapshot;
    setBantuanTableSnapshot(snapshot);
  };

  const fillBreakdownFallback = async (year, aggregated) => {
    let jenis = [];
    try {
      const listRes = await axios.get(
        isMitraMap
          ? NIRLABA_DASHBOARD_LIST_BANTUAN
          : BANTUAN_LIST_TAHUN + year,
        isMitraMap
          ? { timeout: API_TIMEOUT, params: { tahun: year } }
          : { timeout: API_TIMEOUT }
      );
      jenis = Array.isArray(listRes.data?.data) ? listRes.data.data : [];
    } catch {
      return aggregated;
    }
    if (!jenis.length) return aggregated;

    const desilByDesa = new Map();
    (aggregated || []).forEach((row) => {
      desilByDesa.set(String(row.desa || '').trim().toUpperCase(), Number(row.total_desil12) || 0);
    });

    const perJenis = await Promise.all(
      jenis.map((b) =>
        axios
          .get(
            isMitraMap
              ? `${NIRLABA_DASHBOARD_DESA}?tahun=${encodeURIComponent(
                  year
                )}&bantuan=${encodeURIComponent(b)}`
              : `${DESA_BANTUAN_TAHUN}${year}?bantuan=${encodeURIComponent(b)}&lite=1`,
            {
            timeout: API_TIMEOUT,
          })
          .then((r) =>
            (r.data?.data || []).map((row) => ({
              desa: row.desa,
              bantuan: b,
              jumlah_penerima: row.jumlah_penerima || 0,
              total_uang: row.total_uang || 0,
              total_desil12: 0,
              _desaDesil:
                desilByDesa.get(String(row.desa || '').trim().toUpperCase()) || 0,
            }))
          )
          .catch(() => [])
      )
    );
    const flat = perJenis.flat();
    return flat.length ? flat : aggregated;
  };

  const getListDesa = async (year, bantuan) => {
    if (!year) return;
    const params = [];
    if (bantuan && bantuan !== 'all') {
      params.push(`bantuan=${encodeURIComponent(bantuan)}`);
    } else {
      params.push('breakdown=1');
    }
    if (isMitraMap && lembagaId) {
      params.push(`id_lembaga=${encodeURIComponent(lembagaId)}`);
    }
    const url = isMitraMap
      ? `${NIRLABA_DASHBOARD_DESA}?tahun=${encodeURIComponent(year)}&${params.join('&')}`
      : DESA_BANTUAN_TAHUN + year + '?' + params.join('&');
    tahunRef.current = year;
    setIsLoadingMap(true);
    try {
      const res = await axios.get(url, { timeout: API_TIMEOUT });
      if (tahunRef.current !== year) return;
      let data = res.data?.data || [];
      if ((!bantuan || bantuan === 'all') && !hasBantuanBreakdown(data)) {
        data = await fillBreakdownFallback(year, data);
        if (tahunRef.current !== year) return;
      }
      setListDesa(data);
    } catch (error) {
      if (tahunRef.current !== year) return;
      console.error('getListDesa:', error);
      setListDesa([]);
    } finally {
      if (tahunRef.current === year) setIsLoadingMap(false);
    }
  };

  const fetchListBantuan = (year) => {
    if (!year) return;
    axios.get(
      isMitraMap ? NIRLABA_DASHBOARD_LIST_BANTUAN : BANTUAN_LIST_TAHUN + year,
      isMitraMap ? { params: { tahun: year } } : undefined
    )
      .then((res) => {
        const data = res.data?.data || [];
        setListBantuan(Array.isArray(data) ? data : []);
      })
      .catch(() => setListBantuan([]));
  };

  useEffect(() => {
    yearBantuanSummaryRef.current = [];
  }, [tahun]);

  useEffect(() => {
    if (isFixed || isExternal) return;
    setInternalBantuan('all');
    fetchListBantuan(tahun);
  }, [tahun, isFixed, isExternal, isMitraMap]);

  useEffect(() => {
    getListDesa(tahun, selectedBantuan);
  }, [tahun, selectedBantuan, isMitraMap]);

  const layoutKey = layoutSettings
    ? JSON.stringify({
        ml: layoutSettings.mapLevel,
        k: layoutSettings.kecamatanIncludedKeys,
        n: layoutSettings.colors?.none,
        s: layoutSettings.colors?.stroke,
      })
    : '';

  useEffect(() => {
    if (listDesa) {
      combineData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listDesa, layoutKey]);

  useEffect(() => {
    if (!mapCombine) return;

    if (am4ChartRef.current) {
      am4ChartRef.current.dispose();
      am4ChartRef.current = null;
    }

    const map = am4core.create(mapDomId, am4maps.MapChart);
    am4ChartRef.current = map;
    if (map.logo) map.logo.hide();
    setTimeout(() => { if (map.logo) map.logo.hide(); }, 500);
    map.maxZoomLevel = 4;
    map.maxPanOut = 0.75;
    map.geodata = mapCombine;
    map.percentHeight = 90;
    map.dy = 10;
      //   map.projection = new am4maps.projections.AlbersUsa();
      let polygonSeries = map.series.push(new am4maps.MapPolygonSeries());
      polygonSeries.useGeodata = true;
      map.homeZoomLevel = 1;
      map.zoomControl = new am4maps.ZoomControl();
      map.zoomControl.layout = 'horizontal';
      map.zoomControl.align = 'left';
      map.zoomControl.valign = 'bottom';
      map.zoomControl.dy = 30;
      map.zoomControl.dx = 30;
      map.zoomControl.contentHeight = 20;
      map.zoomControl.minusButton.background.fill = am4core.color('#C7D0FF');
      map.zoomControl.minusButton.background.stroke = am4core.color('white');
      map.zoomControl.minusButton.label.fontWeight = 600;
      map.zoomControl.minusButton.label.fontSize = 22;
      map.zoomControl.minusButton.scale = 0.75;
      map.zoomControl.minusButton.label.scale = 0.75;
      map.zoomControl.plusButton.background.fill = am4core.color('#C7D0FF');
      map.zoomControl.plusButton.background.stroke = am4core.color('#6979C9');
      map.zoomControl.plusButton.label.fontWeight = 600;
      map.zoomControl.plusButton.label.fontSize = 22;
      map.zoomControl.plusButton.label.align = 'center';
      map.zoomControl.plusButton.scale = 0.75;
      map.zoomControl.plusButton.label.scale = 0.75;
      map.zoomControl.plusButton.dx = 5;
      let plusButtonHoverState =
        map.zoomControl.plusButton.background.states.create('hover');
      plusButtonHoverState.properties.fill = am4core.color('#354D84');
      let minusButtonHoverState =
        map.zoomControl.minusButton.background.states.create('hover');
      minusButtonHoverState.properties.fill = am4core.color('#354D84');
      let polygonTemplate = polygonSeries.mapPolygons.template;

      polygonTemplate.tooltipHTML = '{DESA}';
      polygonTemplate.adapter.add('tooltipHTML', (_, target) => {
        const ctx = target.dataItem && target.dataItem.dataContext;
        if (!ctx) return '';
        const rincian = ctx.tooltipBantuanRincianHtml || '';
        const wide = rincian ? ' map-tooltip-card-wide' : '';
        const subtitle = ctx.tooltipBantuanSubtitle
          ? `<div class="map-tooltip-caption">${escapeHtml(ctx.tooltipBantuanSubtitle)}</div>`
          : '';
        return `
    <div class="map-tooltip-card${wide}">
      <div class="map-tooltip-title"><strong>${escapeHtml(ctx.DESA || '')}</strong> <span class="map-tooltip-sub">· ${escapeHtml(ctx.KECAMATAN || '')}</span></div>
      ${subtitle}
      <div class="map-tooltip-body">
        <div class="map-tooltip-row">
          <span class="map-tooltip-label">Penerima</span>
          <span class="map-tooltip-value">${escapeHtml(ctx.penerimaBantuan ?? 0)}</span>
        </div>
        <div class="map-tooltip-row">
          <span class="map-tooltip-label">Dana</span>
          <span class="map-tooltip-value map-tooltip-rupiah">${escapeHtml(ctx.totalUangFormatted || formatRupiah(0))}</span>
        </div>
        <div class="map-tooltip-row map-tooltip-highlight">
          <span class="map-tooltip-label">Desil 1+2</span>
          <span class="map-tooltip-value">${escapeHtml(ctx.totalDesil12 ?? 0)}</span>
        </div>
      </div>
      ${rincian}
    </div>
    `;
      });

      //

      //polygonTemplate.propertyFields.fill = 'SYMB';
      polygonSeries.events.on('datavalidated', () => {
        polygonSeries.mapPolygons.each((polygon) => {
          let color = polygon.dataItem.dataContext.color;
          polygon.fill = color;
        });
      });

      //

      //polygonTemplate.fill = am4core.color('red');
      polygonTemplate.stroke = am4core.color(layoutSettings?.colors?.stroke || 'white');
      let hs = polygonTemplate.states.create('hover');
      hs.properties.fill = am4core.color('blue');

      //

      //

      let citySeries = map.series.push(new am4maps.MapImageSeries());
      citySeries.data = cities;
      citySeries.dataFields.value = 'size';
      let city = citySeries.mapImages.template;
      city.nonScaling = true;
      city.propertyFields.latitude = 'latitude';
      city.propertyFields.longitude = 'longitude';
      let circle = city.createChild(am4core.Circle);
      circle.fill = am4core.color('#C7D0FF');
      circle.strokeWidth = 0;
      let circleHoverState = circle.states.create('hover');
      circleHoverState.properties.strokeWidth = 1;
      circle.tooltipText = '{tooltip}';
      circle.propertyFields.radius = 'size';

    return () => {
      if (am4ChartRef.current) {
        am4ChartRef.current.dispose();
        am4ChartRef.current = null;
      }
    };
    // layoutKey mem-fingerprint layoutSettings (termasuk colors.stroke)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapCombine, mapDomId, layoutKey]);

  const toSmallCaps = (t) => (t || '').toString().toLowerCase().replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());

  const legendNoneColor = layoutSettings?.colors?.none || DANA_COLORS.none;

  const handleDownloadPdf = async () => {
    if (!mapWrapRef.current || isLoadingMap || !listDesa.length) return;
    setIsDownloadingPdf(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      let y = 10;

      const addLogoAndSave = async () => {
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.text('Pilihan: Tahun ' + tahun + (selectedBantuan !== 'all' ? ' — ' + selectedBantuan : ''), 14, y);
        y += 10;

        const mapCanvas = await html2canvas(mapWrapRef.current, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
        });
        const mapImgData = mapCanvas.toDataURL('image/png');
        const imgW = pageW - 28;
        const imgH = (mapCanvas.height * imgW) / mapCanvas.width;
        const maxH = pageH - y - 20;
        const finalH = Math.min(imgH, maxH);
        doc.addImage(mapImgData, 'PNG', 14, y, imgW, finalH);
        y += finalH + 12;

        const snap = pdfExportRef.current;
        const perKec = snap && snap.mode === 'kecamatan';
        const withBreakdown = Boolean(snap && snap.breakdown && (snap.groups || []).length);
        const bantuanSummary = snap && Array.isArray(snap.bantuanSummary) ? snap.bantuanSummary : [];

        const drawPdfFooter = () => {
          doc.setFontSize(8);
          doc.setTextColor(120, 120, 120);
          doc.text(
            `Unduh dari Tulufali - ${new Date().toLocaleDateString('id-ID')}`,
            14,
            pageH - 8
          );
        };

        const ensureSpace = (needed) => {
          if (y + needed > pageH - 16) {
            doc.addPage();
            y = 14;
          }
        };

        if (bantuanSummary.length) {
          ensureSpace(28);
          doc.setFontSize(10);
          doc.setTextColor(30, 41, 59);
          doc.text('Rekap Jenis Bantuan — Tahun ' + tahun, 14, y);
          y += 6;

          const sumPenerima = bantuanSummary.reduce(
            (s, r) => s + (Number(r.jumlah_penerima) || 0),
            0
          );
          const sumDesil = bantuanSummary.reduce(
            (s, r) => s + (Number(r.total_desil12) || 0),
            0
          );
          const sumUang = bantuanSummary.reduce(
            (s, r) => s + (Number(r.total_uang) || 0),
            0
          );

          doc.autoTable({
            startY: y,
            head: [['No', 'Jenis Bantuan', 'Total Penerima', 'Desil 1+2', 'Total Dana']],
            body: bantuanSummary.map((row, idx) => [
              String(idx + 1),
              String(row.bantuan || ''),
              Number(row.jumlah_penerima || 0).toLocaleString('id-ID'),
              Number(row.total_desil12 || 0).toLocaleString('id-ID'),
              formatRupiah(row.total_uang),
            ]),
            foot: [
              [
                '',
                'Jumlah',
                sumPenerima.toLocaleString('id-ID'),
                sumDesil.toLocaleString('id-ID'),
                formatRupiah(sumUang),
              ],
            ],
            theme: 'grid',
            headStyles: { fillColor: [25, 118, 210], fontSize: 8 },
            bodyStyles: { fontSize: 7 },
            footStyles: {
              fillColor: [226, 232, 240],
              textColor: [15, 23, 42],
              fontStyle: 'bold',
              fontSize: 7,
            },
            columnStyles: {
              0: { cellWidth: 10, halign: 'center' },
              1: { cellWidth: 78 },
              2: { cellWidth: 28, halign: 'right' },
              3: { cellWidth: 24, halign: 'right' },
              4: { cellWidth: 42, halign: 'right' },
            },
            margin: { left: 14, right: 14 },
            didDrawPage: drawPdfFooter,
          });
          y =
            (doc.lastAutoTable && doc.lastAutoTable.finalY
              ? doc.lastAutoTable.finalY
              : y) + 12;
        }

        ensureSpace(24);
        doc.setFontSize(10);
        doc.setTextColor(30, 41, 59);
        doc.text(
          (perKec ? 'Data per Kecamatan — ' : 'Data per Desa — ') +
            'Distribusi Bantuan Tahun ' +
            tahun +
            (withBreakdown ? ' (rincian jenis)' : ''),
          14,
          y
        );
        y += 8;

        let tableHead;
        let tableBody;
        let columnStyles;
        if (withBreakdown && perKec) {
          tableHead = [['No', 'Kecamatan', 'Jenis Bantuan', 'Penerima', 'Total Dana', 'Desil 1+2']];
          tableBody = [];
          (snap.groups || []).forEach((g, idx) => {
            const items = g.items || [];
            tableBody.push([
              String(idx + 1),
              String(g.label || g.kecKey || ''),
              `Semua jenis (${items.length})`,
              String(g.jumlah_penerima ?? 0),
              formatRupiah(g.total_uang ?? 0),
              String(g.total_desil12 ?? 0),
            ]);
            items.forEach((it) => {
              tableBody.push([
                '',
                '',
                String(it.bantuan || ''),
                String(it.jumlah_penerima ?? 0),
                formatRupiah(it.total_uang ?? 0),
                String(it.total_desil12 ?? 0),
              ]);
            });
          });
          columnStyles = {
            0: { cellWidth: 10, halign: 'center' },
            1: { cellWidth: 40 },
            2: { cellWidth: 48 },
            3: { cellWidth: 22, halign: 'right' },
            4: { cellWidth: 38, halign: 'right' },
            5: { cellWidth: 20, halign: 'right' },
          };
        } else if (withBreakdown) {
          tableHead = [['No', 'Desa', 'Kecamatan', 'Jenis Bantuan', 'Penerima', 'Total Dana', 'Desil 1+2']];
          tableBody = [];
          (snap.groups || []).forEach((g, idx) => {
            const items = g.items || [];
            tableBody.push([
              String(idx + 1),
              String(g.desa || ''),
              String(g.kecamatan || getKecamatan(g.desa)),
              `Semua jenis (${items.length})`,
              String(g.jumlah_penerima ?? 0),
              formatRupiah(g.total_uang ?? 0),
              String(g.total_desil12 ?? 0),
            ]);
            items.forEach((it) => {
              tableBody.push([
                '',
                '',
                '',
                String(it.bantuan || ''),
                String(it.jumlah_penerima ?? 0),
                formatRupiah(it.total_uang ?? 0),
                String(it.total_desil12 ?? 0),
              ]);
            });
          });
          columnStyles = {
            0: { cellWidth: 8, halign: 'center' },
            1: { cellWidth: 28 },
            2: { cellWidth: 28 },
            3: { cellWidth: 42 },
            4: { cellWidth: 18, halign: 'right' },
            5: { cellWidth: 34, halign: 'right' },
            6: { cellWidth: 18, halign: 'right' },
          };
        } else if (perKec) {
          tableHead = [['No', 'Kecamatan', 'Penerima', 'Total Dana', 'Desil 1+2']];
          tableBody = (snap.rows || []).map((row, idx) => [
            String(idx + 1),
            String(row.label || row.kecKey || ''),
            String(row.jumlah_penerima ?? 0),
            formatRupiah(row.total_uang ?? 0),
            String(row.total_desil12 ?? 0),
          ]);
          columnStyles = {
            0: { cellWidth: 12, halign: 'center' },
            1: { cellWidth: 52 },
            2: { cellWidth: 24, halign: 'right' },
            3: { cellWidth: 46, halign: 'right' },
            4: { cellWidth: 24, halign: 'right' },
          };
        } else {
          tableHead = [['No', 'Desa', 'Kecamatan', 'Penerima', 'Total Dana', 'Desil 1+2']];
          tableBody = (snap.rows || []).map((row, idx) => [
            String(idx + 1),
            (row.desa || '').toString(),
            getKecamatan(row.desa),
            String(row.jumlah_penerima ?? 0),
            formatRupiah(row.total_uang ?? 0),
            String(row.total_desil12 ?? 0),
          ]);
          columnStyles = {
            0: { cellWidth: 10, halign: 'center' },
            1: { cellWidth: 38 },
            2: { cellWidth: 35 },
            3: { cellWidth: 22, halign: 'right' },
            4: { cellWidth: 42, halign: 'right' },
            5: { cellWidth: 22, halign: 'right' },
          };
        }
        doc.autoTable({
          startY: y,
          head: tableHead,
          body: tableBody,
          theme: 'grid',
          headStyles: { fillColor: [25, 118, 210], fontSize: 8 },
          bodyStyles: { fontSize: 7 },
          columnStyles,
          margin: { left: 14 },
          didDrawPage: drawPdfFooter,
        });

        const bantuanSlug = (selectedBantuan && selectedBantuan !== 'all')
          ? `-${String(selectedBantuan).replace(/\s+/g, '-').slice(0, 40)}`
          : '';
        doc.save(`Peta-Distribusi-Bantuan-${tahun}${bantuanSlug}-${new Date().toLocaleDateString('id-ID').replace(/\//g, '-')}.pdf`);
        setIsDownloadingPdf(false);
      };

      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const logoData = canvas.toDataURL('image/png');
          doc.addImage(logoData, 'PNG', 14, y, 10, 10);
          doc.setFontSize(7);
          doc.setTextColor(80, 80, 80);
          doc.text(TAGLINE, 25, y + 6, { maxWidth: pageW - 30 });
        } catch (_) {
          doc.setFontSize(7);
          doc.text(TAGLINE, 14, y + 5);
        }
        y += 18;
        addLogoAndSave();
      };
      img.onerror = () => {
        doc.setFontSize(7);
        doc.text(TAGLINE, 14, y + 5);
        y += 18;
        addLogoAndSave();
      };
      img.src = TulufaliLogo;
    } catch (err) {
      console.error('handleDownloadPdf:', err);
      setIsDownloadingPdf(false);
    }
  };

  const downloadPdfHandlerRef = useRef(handleDownloadPdf);
  downloadPdfHandlerRef.current = handleDownloadPdf;
  useImperativeHandle(ref, () => ({
    downloadPdf: () => Promise.resolve(downloadPdfHandlerRef.current()),
  }), []);

  const rootClass = [s.mapChart, customizationEmbed ? s.mapChartCustomization : '']
    .filter(Boolean)
    .join(' ');

  const showBantuanBreakdown = Boolean(
    bantuanTableSnapshot.breakdown && (bantuanTableSnapshot.groups || []).length
  );
  const breakdownSuffix = showBantuanBreakdown ? ' — rincian jenis bantuan' : '';

  return (
    <div className={rootClass}>
      <div className={s.mapWrap} ref={mapWrapRef}>
        {!hideMapChrome && (
          <div className={s.mapStats}>
            <div className={s.mapStatsLeft}>
              {compactHeader && isFixed ? (
                <p className={s.mapStatsYear} style={{ margin: 0, opacity: 0.75 }}>
                  Tahun {tahun}
                </p>
              ) : (
                <>
                  <h6 className={s.mapStatsTitle}>
                    {isMitraMap
                      ? 'SEBARAN BANTUAN MITRA PER DESA'
                      : 'SEBARAN BANTUAN PER DESA'}
                  </h6>
                  <p className={s.mapStatsYear}>
                    Tahun {tahun}
                    {isFixed && fixedBantuan ? ` — ${fixedBantuan}` : ''}
                  </p>
                </>
              )}
            </div>
            <div className={s.mapSelectWrap} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {!isFixed && (
                <Input
                  type="select"
                  value={isExternal ? externalBantuan.value : internalBantuan}
                  onChange={(e) =>
                    isExternal ? externalBantuan.onChange(e.target.value) : setInternalBantuan(e.target.value)
                  }
                  className={s.mapSelect}
                >
                  <option value="all">Semua Bantuan</option>
                  {(isExternal ? externalBantuan.options : listBantuan).map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </Input>
              )}
              <Button
                color="light"
                size="sm"
                onClick={handleDownloadPdf}
                disabled={isDownloadingPdf || isLoadingMap || !listDesa.length}
                className={s.btnDownloadPdf}
              >
                <i className={`fa fa-file-pdf-o ${s.pdfIcon}`} aria-hidden="true" />
                {isDownloadingPdf ? ' ...' : ' PDF'}
              </Button>
            </div>
          </div>
        )}
        {isLoadingMap && (
          <div className={s.mapOverlay}>
            <div className={s.mapSpinner} />
            <span className={s.mapLoadingText}>Memuat data peta...</span>
          </div>
        )}
        <div className={s.map} id={mapDomId} />
        <div className={s.mapLegend}>
          <div className={s.legendItem}>
            <span className={s.legendColor} style={{ background: legendNoneColor }} />
            <span className={s.legendLabel}>Tidak ada bantuan sosial</span>
          </div>
          <div className={s.legendItem}>
            <span className={s.legendColor} style={{ background: DANA_COLORS.low }} />
            <span className={s.legendLabel}>
              {danaThresholds.threshold1 > 0
                ? `Rp 1 – ${formatRupiah(danaThresholds.threshold1)}`
                : '—'}
            </span>
          </div>
          <div className={s.legendItem}>
            <span className={s.legendColor} style={{ background: DANA_COLORS.medium }} />
            <span className={s.legendLabel}>
              {danaThresholds.threshold1 < danaThresholds.threshold2
                ? `${formatRupiah(danaThresholds.threshold1 + 1)} – ${formatRupiah(danaThresholds.threshold2)}`
                : '—'}
            </span>
          </div>
          <div className={s.legendItem}>
            <span className={s.legendColor} style={{ background: DANA_COLORS.high }} />
            <span className={s.legendLabel}>
              {danaThresholds.threshold2 > 0
                ? `> ${formatRupiah(danaThresholds.threshold2)}`
                : '—'}
            </span>
          </div>
        </div>
      </div>

      {listDesa.length > 0 && !hidePerDesaTable && (
        <div className={s.mapDataTableWrap}>
          <h6 className={s.mapDataTableTitle}>
            {compactHeader && isFixed
              ? bantuanTableSnapshot.mode === 'kecamatan'
                ? `Per kecamatan — ${tahun}`
                : `Per desa — ${tahun}`
              : bantuanTableSnapshot.mode === 'kecamatan'
                ? `Data per Kecamatan — Tahun ${tahun}${
                    selectedBantuan !== 'all' ? ` (${selectedBantuan})` : breakdownSuffix
                  }`
                : `Data per Desa — Tahun ${tahun}${
                    selectedBantuan !== 'all' ? ` (${selectedBantuan})` : breakdownSuffix
                  }`}
          </h6>
          <div className={`${s.mapDataTableScroll} ${showBantuanBreakdown ? s.mapDataTableScrollTall : ''}`}>
            <Table responsive bordered size="sm" className={s.mapDataTable}>
              <thead>
                <tr>
                  <th className={s.thNo}>No</th>
                  {bantuanTableSnapshot.mode === 'kecamatan' ? (
                    <th>Kecamatan</th>
                  ) : (
                    <>
                      <th>Desa</th>
                      <th>Kecamatan</th>
                    </>
                  )}
                  {showBantuanBreakdown && <th>Jenis Bantuan</th>}
                  <th className={s.thNum}>Penerima</th>
                  <th className={s.thNum}>Total Dana</th>
                  <th className={s.thNum}>Desil 1+2</th>
                </tr>
              </thead>
              <tbody>
                {showBantuanBreakdown && bantuanTableSnapshot.mode === 'kecamatan'
                  ? (bantuanTableSnapshot.groups || []).map((g, idx) => {
                      const items = g.items || [];
                      const span = items.length + 1;
                      return (
                        <React.Fragment key={g.kecKey || idx}>
                          <tr className={s.rowDesaTotal}>
                            <td className={s.thNo} rowSpan={span}>
                              {idx + 1}
                            </td>
                            <td rowSpan={span}>{toSmallCaps(g.label || g.kecKey)}</td>
                            <td className={s.jenisTotalLabel}>
                              Semua jenis ({items.length})
                            </td>
                            <td className={s.thNum}>
                              {(g.jumlah_penerima ?? 0).toLocaleString('id-ID')}
                            </td>
                            <td className={s.thNum}>{formatRupiah(g.total_uang)}</td>
                            <td className={s.thNum}>
                              {(g.total_desil12 ?? 0).toLocaleString('id-ID')}
                            </td>
                          </tr>
                          {items.map((it) => (
                            <tr
                              key={`${g.kecKey}-${it.bantuan}`}
                              className={s.rowBantuanDetail}
                            >
                              <td>{toSmallCaps(it.bantuan)}</td>
                              <td className={s.thNum}>
                                {(it.jumlah_penerima ?? 0).toLocaleString('id-ID')}
                              </td>
                              <td className={s.thNum}>{formatRupiah(it.total_uang)}</td>
                              <td className={s.thNum}>
                                {(it.total_desil12 ?? 0).toLocaleString('id-ID')}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })
                  : showBantuanBreakdown
                  ? (bantuanTableSnapshot.groups || []).map((g, idx) => {
                      const items = g.items || [];
                      const span = items.length + 1;
                      return (
                        <React.Fragment key={`${g.desa}-${idx}`}>
                          <tr className={s.rowDesaTotal}>
                            <td className={s.thNo} rowSpan={span}>
                              {idx + 1}
                            </td>
                            <td rowSpan={span}>{toSmallCaps(g.desa)}</td>
                            <td rowSpan={span}>{toSmallCaps(g.kecamatan)}</td>
                            <td className={s.jenisTotalLabel}>
                              Semua jenis ({items.length})
                            </td>
                            <td className={s.thNum}>
                              {(g.jumlah_penerima ?? 0).toLocaleString('id-ID')}
                            </td>
                            <td className={s.thNum}>{formatRupiah(g.total_uang)}</td>
                            <td className={s.thNum}>
                              {(g.total_desil12 ?? 0).toLocaleString('id-ID')}
                            </td>
                          </tr>
                          {items.map((it) => (
                            <tr
                              key={`${g.desa}-${it.bantuan}`}
                              className={s.rowBantuanDetail}
                            >
                              <td>{toSmallCaps(it.bantuan)}</td>
                              <td className={s.thNum}>
                                {(it.jumlah_penerima ?? 0).toLocaleString('id-ID')}
                              </td>
                              <td className={s.thNum}>{formatRupiah(it.total_uang)}</td>
                              <td className={s.thNum}>
                                {(it.total_desil12 ?? 0).toLocaleString('id-ID')}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })
                  : bantuanTableSnapshot.mode === 'kecamatan'
                  ? bantuanTableSnapshot.rows.map((row, idx) => (
                      <tr key={row.kecKey || idx}>
                        <td className={s.thNo}>{idx + 1}</td>
                        <td>{toSmallCaps(row.label || row.kecKey)}</td>
                        <td className={s.thNum}>
                          {(row.jumlah_penerima ?? 0).toLocaleString('id-ID')}
                        </td>
                        <td className={s.thNum}>{formatRupiah(row.total_uang)}</td>
                        <td className={s.thNum}>
                          {(row.total_desil12 ?? 0).toLocaleString('id-ID')}
                        </td>
                      </tr>
                    ))
                  : bantuanTableSnapshot.rows.map((row, idx) => (
                      <tr key={`${row.desa}-${idx}`}>
                        <td className={s.thNo}>{idx + 1}</td>
                        <td>{toSmallCaps(row.desa)}</td>
                        <td>{toSmallCaps(getKecamatan(row.desa))}</td>
                        <td className={s.thNum}>
                          {(row.jumlah_penerima ?? 0).toLocaleString('id-ID')}
                        </td>
                        <td className={s.thNum}>{formatRupiah(row.total_uang)}</td>
                        <td className={s.thNum}>
                          {(row.total_desil12 ?? 0).toLocaleString('id-ID')}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
});

Am4chartMap.displayName = 'Am4chartMap';

export default Am4chartMap;
